import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, Select,
  MenuItem, FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert,
} from '@mui/material';
import { PlayArrow, Delete, Visibility } from '@mui/icons-material';
import dayjs from '../utils/dayjs';
import toast from 'react-hot-toast';
import { backtest, settings } from '../services/api';

function ResultCard({ label, value, color }) {
  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" fontWeight={700} sx={{ color }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function BacktestPage() {
  const [runs, setRuns] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [form, setForm] = useState({
    strategyId: 1, symbol: 'NIFTY', fromDate: '', toDate: '', initialCapital: 100000,
    minConfidencePct: 90, cooldownMinutes: 30,
  });

  const loadRuns = async () => {
    setLoading(true);
    try {
      const [runsRes, stratRes] = await Promise.all([backtest.runs(), settings.getStrategies()]);
      setRuns(runsRes.data.data);
      setStrategies(stratRes.data.data);
      if (stratRes.data.data.length > 0) setForm(f => ({ ...f, strategyId: stratRes.data.data[0].id }));
    } catch { /* handled */ } finally { setLoading(false); }
  };

  useEffect(() => { loadRuns(); }, []);

  const handleCreate = async () => {
    try {
      await backtest.create(form);
      toast.success('Backtest started!');
      setFormOpen(false);
      setTimeout(loadRuns, 2000);
    } catch { /* handled */ }
  };

  const handleDelete = async (id) => {
    await backtest.delete(id);
    toast.success('Deleted');
    loadRuns();
  };

  const handleView = async (id) => {
    const res = await backtest.get(id);
    setSelectedRun(res.data.data);
    setDetailOpen(true);
  };

  const getStatusColor = (s) => ({ COMPLETED: 'success', RUNNING: 'warning', FAILED: 'error', PENDING: 'default' }[s] || 'default');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Backtesting</Typography>
        <Button variant="contained" startIcon={<PlayArrow />} onClick={() => setFormOpen(true)}>New Backtest</Button>
      </Box>

      {loading ? <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={60} /></Box> : (
        <Card sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: '#f5f5f5' } }}>
                  <TableCell>ID</TableCell><TableCell>Strategy</TableCell><TableCell>Symbol</TableCell>
                  <TableCell>Period</TableCell><TableCell>Status</TableCell>
                  <TableCell align="right">Min Conf.</TableCell><TableCell align="right">Cooldown</TableCell>
                  <TableCell align="right">Trades</TableCell><TableCell align="right">Trades/Day</TableCell>
                  <TableCell align="right">Win Rate</TableCell><TableCell align="right">Net P&L</TableCell>
                  <TableCell align="right">Profit Factor</TableCell><TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.length === 0 ? (
                  <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4 }}>No backtest runs yet. Click "New Backtest" to start.</TableCell></TableRow>
                ) : runs.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>#{r.id}</TableCell>
                    <TableCell>{r.strategy_name}</TableCell>
                    <TableCell><Chip size="small" label={r.symbol} color="primary" /></TableCell>
                    <TableCell>{dayjs(r.from_date).format('DD/MM/YY')} - {dayjs(r.to_date).format('DD/MM/YY')}</TableCell>
                    <TableCell><Chip size="small" label={r.status} color={getStatusColor(r.status)} /></TableCell>
                    <TableCell align="right">{parseFloat(r.min_confidence_pct ?? 90).toFixed(0)}%</TableCell>
                    <TableCell align="right">{r.cooldown_minutes ?? 30}m</TableCell>
                    <TableCell align="right">{r.total_trades}</TableCell>
                    <TableCell align="right">{r.trades_per_day ? parseFloat(r.trades_per_day).toFixed(1) : '—'}</TableCell>
                    <TableCell align="right">{r.win_rate ? `${parseFloat(r.win_rate).toFixed(1)}%` : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: parseFloat(r.net_pnl) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>
                      {r.net_pnl ? `₹${parseFloat(r.net_pnl).toFixed(0)}` : '—'}
                    </TableCell>
                    <TableCell align="right">{r.profit_factor ? parseFloat(r.profit_factor).toFixed(2) : '—'}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => handleView(r.id)} disabled={r.status !== 'COMPLETED'} startIcon={<Visibility />}>View</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(r.id)} startIcon={<Delete />}>Del</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Backtest Run</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Strategy</InputLabel>
                <Select value={form.strategyId} label="Strategy" onChange={e => setForm({ ...form, strategyId: e.target.value })}>
                  {strategies.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Symbol</InputLabel>
                <Select value={form.symbol} label="Symbol" onChange={e => setForm({ ...form, symbol: e.target.value })}>
                  <MenuItem value="NIFTY">NIFTY</MenuItem><MenuItem value="BANKNIFTY">BANKNIFTY</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Initial Capital (₹)" value={form.initialCapital} onChange={e => setForm({ ...form, initialCapital: e.target.value })} />
            </Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="From Date" InputLabelProps={{ shrink: true }} value={form.fromDate} onChange={e => setForm({ ...form, fromDate: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="To Date" InputLabelProps={{ shrink: true }} value={form.toDate} onChange={e => setForm({ ...form, toDate: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="number" label="Min Confidence %" value={form.minConfidencePct}
                onChange={e => setForm({ ...form, minConfidencePct: e.target.value })}
                helperText="90 = matches live bot's technical bar. Lower to test a higher-frequency variant."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="number" label="Cooldown (minutes)" value={form.cooldownMinutes}
                onChange={e => setForm({ ...form, cooldownMinutes: e.target.value })}
                helperText="30 = matches live bot. Lower to allow more re-entries per day."
              />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>Backtest requires historical OHLCV data in the database for the selected period.</Alert>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Confidence here is <b>technical-only</b> (EMA/RSI/MACD/ADX/VWAP/Volume) — the live bot also requires News ≥60% and OI/PCR alignment, which have no historical data to replay. Treat results as a directional comparison between settings, not an exact live match.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.fromDate || !form.toDate} startIcon={<PlayArrow />}>Start Backtest</Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Backtest Results #{selectedRun?.id}</DialogTitle>
        <DialogContent>
          {selectedRun && (
            <>
              <Box sx={{ mb: 2 }}>
                <Chip size="small" sx={{ mr: 1 }} label={`Min Confidence: ${parseFloat(selectedRun.min_confidence_pct ?? 90).toFixed(0)}%`} />
                <Chip size="small" sx={{ mr: 1 }} label={`Cooldown: ${selectedRun.cooldown_minutes ?? 30}m`} />
                <Chip size="small" label={`${parseFloat(selectedRun.trades_per_day || 0).toFixed(1)} trades/day avg`} color="primary" />
              </Box>
              <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
                <Grid item xs={6} sm={3}><ResultCard label="Total Trades" value={selectedRun.total_trades} /></Grid>
                <Grid item xs={6} sm={3}><ResultCard label="Win Rate" value={`${parseFloat(selectedRun.win_rate || 0).toFixed(1)}%`} color={parseFloat(selectedRun.win_rate) >= 50 ? '#2e7d32' : '#c62828'} /></Grid>
                <Grid item xs={6} sm={3}><ResultCard label="Net P&L" value={`₹${parseFloat(selectedRun.net_pnl || 0).toFixed(0)}`} color={parseFloat(selectedRun.net_pnl) >= 0 ? '#2e7d32' : '#c62828'} /></Grid>
                <Grid item xs={6} sm={3}><ResultCard label="Profit Factor" value={parseFloat(selectedRun.profit_factor || 0).toFixed(2)} /></Grid>
                <Grid item xs={6} sm={3}><ResultCard label="Max Drawdown" value={`₹${parseFloat(selectedRun.max_drawdown || 0).toFixed(0)}`} color="#c62828" /></Grid>
                <Grid item xs={6} sm={3}><ResultCard label="Sharpe Ratio" value={parseFloat(selectedRun.sharpe_ratio || 0).toFixed(2)} /></Grid>
                <Grid item xs={6} sm={3}><ResultCard label="Best Trade" value={`₹${parseFloat(selectedRun.best_trade || 0).toFixed(0)}`} color="#2e7d32" /></Grid>
                <Grid item xs={6} sm={3}><ResultCard label="Worst Trade" value={`₹${parseFloat(selectedRun.worst_trade || 0).toFixed(0)}`} color="#c62828" /></Grid>
              </Grid>
              {selectedRun.trades?.length > 0 && (
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5' } }}>
                        <TableCell>Entry</TableCell><TableCell>Type</TableCell><TableCell align="right">Strike</TableCell>
                        <TableCell align="right">Confidence</TableCell><TableCell align="right">Net P&L</TableCell><TableCell>Exit Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedRun.trades.map(t => (
                        <TableRow key={t.id} hover>
                          <TableCell>{dayjs(t.entry_time).format('DD/MM HH:mm')}</TableCell>
                          <TableCell><Chip size="small" label={t.instrument_type} color={t.instrument_type === 'CE' ? 'success' : 'error'} /></TableCell>
                          <TableCell align="right">{t.strike_price}</TableCell>
                          <TableCell align="right">{t.confidence != null ? `${parseFloat(t.confidence).toFixed(0)}%` : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(t.net_pnl) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                            ₹{parseFloat(t.net_pnl).toFixed(0)}
                          </TableCell>
                          <TableCell>{t.exit_reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
